import { cn } from '@/utils/cn'
import type { SuggestionChip } from '@/types'
import { CARE_ADVOCATE_PHONE, CARE_ADVOCATE_TEL_HREF } from '@/utils/constants'

export interface SuggestionChipItemProps {
  text: string
  kind?: SuggestionChip['kind']
  /** Destination for a "link" chip, e.g. a member portal page. */
  url?: string | null
  onSelect: (text: string) => void
  disabled?: boolean
}

// Every chip is the same pill whether it renders as a button or an anchor.
const pillStyles = cn(
  'rounded-full border border-primary-200 bg-white px-3 py-1.5 text-sm text-primary-700 cursor-pointer',
  'transition-colors hover:bg-primary-50',
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
)

const linkPillStyles = cn(pillStyles, 'no-underline')

// The `disabled:` variants match the HTML `disabled` attribute, which only a
// button can carry.
const buttonPillStyles = cn(
  pillStyles,
  'disabled:cursor-not-allowed disabled:opacity-50',
)

// Chip URLs reach the client already absolutized against the member portal, so
// only an absolute http(s) destination is rendered as a link. Anything else
// falls back to a plain chip: a `javascript:` or `data:` URL, a
// protocol-relative host, or a relative path, which would resolve against this
// widget's own origin rather than the portal.
function isResolvingToHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * One tappable chip, shared by the suggestion chips under an assistant message
 * and the welcome-screen starter prompts. The element follows `kind`:
 *
 * - `handoff` dials a care advocate through a `tel:` anchor aimed at the
 *   top-level context. The chat runs in an iframe, where iOS Safari reaches the
 *   dialer only from an anchor activation that leaves the frame; a scripted
 *   location assignment is dropped. The OS claims the `tel:` scheme, so the top
 *   document is never navigated away and no tab is opened. This chip ignores
 *   `disabled`, so reaching a care advocate is always available.
 * - `link` opens `url` in a new tab, provided it is an absolute http(s) URL.
 *   Such a chip also ignores `disabled`, since opening the member portal does
 *   not interact with the reply in flight; a link chip without a usable URL
 *   falls back to the plain chip below and disables with it.
 * - anything else sends `text` as the next chat message.
 */
export function SuggestionChipItem({
  text,
  kind = 'answerable',
  url,
  onSelect,
  disabled = false,
}: SuggestionChipItemProps) {
  if (kind === 'handoff') {
    return (
      <a
        href={CARE_ADVOCATE_TEL_HREF}
        target="_top"
        aria-label={`${text}, call ${CARE_ADVOCATE_PHONE}`}
        className={linkPillStyles}
      >
        {text}
      </a>
    )
  }

  // Link chips are real anchors so they get native new-tab semantics
  // (middle-click, copy link, screen-reader link role).
  if (kind === 'link' && url && isResolvingToHttpUrl(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkPillStyles}
      >
        {text}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
    )
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(text)}
      className={buttonPillStyles}
    >
      {text}
    </button>
  )
}
